import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://agro-b2b-leads.preview.emergentagent.com";
const DEFAULT_IMAGE = "/assets/sharda-logo.jpg";

const toTitle = (value) =>
  value
    .split("-")
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(" ");

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const upsertLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const getPageMeta = (pathname, content) => {
  const isHindi = content.locale === "hi";
  const sharedKeywords = isHindi
    ? [
        "mushroom supplier chhattisgarh",
        "button mushroom baloda bazar",
        "fresh mushrooms raipur",
        "fresh mushrooms bilaspur",
        "fresh mushrooms durg bhilai",
        "mushroom supplier odisha",
        "fresh mushrooms rourkela",
        "fresh mushrooms sambalpur",
        "mushroom supplier nearby states",
        "mushroom spawn chhattisgarh",
        "premium compost chhattisgarh",
        "Sharda Agrotech",
      ]
    : [
        "fresh mushrooms in chhattisgarh",
        "button mushroom supplier baloda bazar",
        "fresh mushrooms in raipur",
        "fresh mushrooms in bilaspur",
        "fresh mushrooms in durg bhilai",
        "mushroom supplier in odisha",
        "fresh mushrooms in rourkela",
        "fresh mushrooms in sambalpur",
        "mushroom supplier nearby cities",
        "mushroom spawn chhattisgarh",
        "premium compost chhattisgarh",
        "Sharda Agrotech",
      ];

  if (pathname === "/") {
    return {
      title: isHindi
        ? "Sharda Agrotech | ताज़े मशरूम सप्लाई, स्पॉन और प्रीमियम कम्पोस्ट"
        : "Sharda Agrotech | Fresh Mushroom Supply, Spawn & Premium Compost in Chhattisgarh & Odisha",
      description: isHindi
        ? "Sharda Agrotech छत्तीसगढ़, ओडिशा और आसपास के शहरों में ताज़े मशरूम, मशरूम स्पॉन और प्रीमियम कम्पोस्ट की भरोसेमंद सप्लाई देता है। Baloda Bazar, Raipur और क्षेत्रीय खरीदारों के लिए पूछताछ आधारित सहायता उपलब्ध है।"
        : "Sharda Agrotech offers fresh mushroom supply, mushroom spawn, and premium compost across Chhattisgarh, Odisha, and nearby cities with inquiry-first support for buyers in Baloda Bazar, Raipur, and regional markets.",
      keywords: sharedKeywords,
      type: "website",
    };
  }

  if (pathname === "/products") {
    return {
      title: isHindi
        ? "उत्पाद | ताज़े मशरूम, स्पॉन और कम्पोस्ट | Sharda Agrotech"
        : "Products | Fresh Mushrooms, Mushroom Spawn & Compost | Sharda Agrotech",
      description: isHindi
        ? "Sharda Agrotech के ताज़े मशरूम, लैब-क्वालिटी स्पॉन और प्रीमियम कम्पोस्ट देखें। छत्तीसगढ़ के खरीदारों और ग्रोअर्स के लिए पूछताछ आधारित सप्लाई उपलब्ध है।"
        : "Explore Sharda Agrotech fresh mushrooms, lab-quality mushroom spawn, and premium compost for buyers and growers across Chhattisgarh, Odisha, and nearby cities.",
      keywords: [...sharedKeywords, "fresh mushrooms products", "mushroom compost supplier"],
      type: "website",
    };
  }

  if (pathname.startsWith("/products/")) {
    const slug = pathname.split("/").pop() || "product";
    const title = toTitle(slug);
    return {
      title: `${title} | Sharda Agrotech`,
      description: isHindi
        ? `Sharda Agrotech से ${title} के लिए पूछताछ करें। छत्तीसगढ़ में गुणवत्ता, ताज़गी और भरोसेमंद सप्लाई पर आधारित सहायता उपलब्ध है।`
        : `Inquire with Sharda Agrotech for ${title}. Quality-led, fresh, and dependable supply support across Chhattisgarh.`,
      keywords: [...sharedKeywords, slug.replaceAll("-", " ")],
      type: "product",
    };
  }

  if (pathname === "/wholesale") {
    return {
      title: isHindi
        ? "थोक सप्लाई | Sharda Agrotech"
        : "Wholesale Supply | Sharda Agrotech",
      description: isHindi
        ? "रेस्टोरेंट, होटल, कैटरर, थोक खरीदारों और ग्रोअर्स के लिए ताज़े मशरूम, स्पॉन और कम्पोस्ट की पूछताछ आधारित सप्लाई।"
        : "Inquiry-first wholesale supply of fresh mushrooms, spawn, and premium compost for restaurants, hotels, caterers, wholesalers, and growers across Chhattisgarh, Odisha, and nearby cities.",
      keywords: [...sharedKeywords, "wholesale mushrooms chhattisgarh", "mushroom supplier for hotels"],
      type: "website",
    };
  }

  if (pathname === "/about") {
    return {
      title: isHindi ? "हमारे बारे में | Sharda Agrotech" : "About | Sharda Agrotech",
      description: isHindi
        ? "Sharda Agrotech के बारे में जानें — ताज़गी, भरोसे और वैज्ञानिक खेती पर आधारित मशरूम फार्म ब्रांड।"
        : "Learn about Sharda Agrotech, a fresh mushroom farm brand built on trust, scientific cultivation, and dependable supply across Chhattisgarh, Odisha, and nearby markets.",
      keywords: [...sharedKeywords, "about sharda agrotech", "mushroom farm chhattisgarh"],
      type: "website",
    };
  }

  if (pathname === "/process") {
    return {
      title: isHindi ? "प्रक्रिया | Sharda Agrotech" : "Process | Sharda Agrotech",
      description: isHindi
        ? "कम्पोस्टिंग से डिस्पैच तक Sharda Agrotech की प्रक्रिया देखें, जो ताज़गी, गुणवत्ता और भरोसेमंद सप्लाई के लिए बनाई गई है।"
        : "See the Sharda Agrotech process from composting to dispatch, built for freshness, quality, and dependable supply.",
      keywords: [...sharedKeywords, "mushroom growing process", "mushroom composting and spawning"],
      type: "website",
    };
  }

  if (pathname === "/blog") {
    return {
      title: isHindi ? "ब्लॉग | Sharda Agrotech" : "Blog | Sharda Agrotech",
      description: isHindi
        ? "ताज़े मशरूम, स्टोरेज, स्पॉन और कम्पोस्ट पर उपयोगी ब्लॉग और गाइड पढ़ें।"
        : "Read practical blog articles and guides on fresh mushrooms, storage, spawn, and compost from Sharda Agrotech.",
      keywords: [...sharedKeywords, "mushroom blog", "mushroom storage guide"],
      type: "article",
    };
  }

  if (pathname.startsWith("/blog/")) {
    const slug = pathname.split("/").pop() || "blog";
    const title = toTitle(slug);
    return {
      title: `${title} | Sharda Agrotech Blog`,
      description: isHindi
        ? `${title} पर Sharda Agrotech का लेख पढ़ें। ताज़े मशरूम, स्पॉन, कम्पोस्ट और सप्लाई से जुड़े उपयोगी सुझाव।`
        : `Read the Sharda Agrotech article on ${title}. Useful insights on fresh mushrooms, spawn, compost, and reliable supply.`,
      keywords: [...sharedKeywords, title],
      type: "article",
    };
  }

  return {
    title: isHindi ? "संपर्क | Sharda Agrotech" : "Contact | Sharda Agrotech",
    description: isHindi
      ? "Sharda Agrotech से कॉल, व्हाट्सऐप, ईमेल या पूछताछ फ़ॉर्म के माध्यम से संपर्क करें।"
        : "Contact Sharda Agrotech by call, WhatsApp, email, or inquiry form for fresh mushrooms, spawn, and premium compost across Chhattisgarh, Odisha, and nearby cities.",
    keywords: [...sharedKeywords, "contact mushroom supplier"],
    type: "website",
  };
};

export function SeoManager({ content }) {
  const location = useLocation();

  useEffect(() => {
    const meta = getPageMeta(location.pathname, content);
    const canonicalUrl = `${SITE_URL}${location.pathname}`;

    document.title = meta.title;
    document.documentElement.lang = content.locale;

    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: meta.keywords.join(", ") });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow, max-image-preview:large" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: meta.type });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: `${SITE_URL}${DEFAULT_IMAGE}` });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    let schemaScript = document.getElementById("seo-structured-data");
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.id = "seo-structured-data";
      schemaScript.type = "application/ld+json";
      document.head.appendChild(schemaScript);
    }

    schemaScript.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Sharda Agrotech",
        url: SITE_URL,
        logo: `${SITE_URL}${DEFAULT_IMAGE}`,
        email: "shardaagrotech@outlook.com",
        telephone: "+91-9575509777",
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Sharda Agrotech",
        description: meta.description,
        image: `${SITE_URL}${DEFAULT_IMAGE}`,
        url: canonicalUrl,
        telephone: "+91-9575509777",
        email: "shardaagrotech@outlook.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Village Khainda",
          addressLocality: "Baloda Bazar",
          addressRegion: "Chhattisgarh",
          postalCode: "493332",
          addressCountry: "IN",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Sharda Agrotech",
        url: SITE_URL,
        inLanguage: content.locale === "hi" ? "hi-IN" : "en-IN",
      },
    ]);
  }, [content, location.pathname]);

  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) {
        badge.remove();
      }

      document.querySelectorAll("a").forEach((link) => {
        if (link.textContent?.includes("Made with Emergent")) {
          link.remove();
        }
      });
    };

    removeBadge();
    const interval = window.setInterval(removeBadge, 500);

    return () => window.clearInterval(interval);
  }, []);

  return null;
}